/**
 * Which tickets are new since the last look.
 *
 * WHY THIS IS NOT JUST "THE LIST GOT LONGER"
 * ------------------------------------------
 * The board is filtered by station and re-fetched every few seconds, and
 * tickets leave it as the kitchen finishes them. A count that went from four to
 * five could be one order arriving, or two arriving while one was completed —
 * which would announce one order and swallow the other.
 *
 * So arrivals are worked out by identity, not by counting.
 *
 * THE FIRST LOOK IS SILENT
 * ------------------------
 * Every ticket on the board is new the first time the board is drawn. Chiming
 * for all of them would mean a burst of noise every time the screen is opened
 * or the station filter is changed, which teaches a kitchen to ignore the
 * sound — and the sound is the entire point.
 */

/**
 * @param {{id: string}[]} tickets  what is on the board now
 * @param {Set<string>} seen        ids from the previous look; mutated
 * @param {boolean} firstLook       true when nothing has been seen yet
 * @returns {{id: string}[]} the ones worth making a noise about
 */
export function arrivalsAmong(tickets, seen, firstLook) {
  const arrived = [];

  for (const ticket of tickets) {
    if (!ticket?.id) continue;
    if (!seen.has(ticket.id)) {
      seen.add(ticket.id);
      if (!firstLook) arrived.push(ticket);
    }
  }

  /**
   * Tickets the kitchen has finished with are forgotten, so the set cannot grow
   * without limit on a screen left running for a week. Forgetting is safe here:
   * a ticket that has left the board and comes back — an order sent again after
   * a round is added — is genuinely something the kitchen has to cook again.
   */
  const present = new Set(tickets.map((t) => t?.id));
  for (const id of seen) {
    if (!present.has(id)) seen.delete(id);
  }

  return arrived;
}

/** How the arrival reads on screen. */
export function announce(arrived) {
  if (arrived.length === 0) return null;
  if (arrived.length === 1) {
    const one = arrived[0];
    const where = one.table_number ? `Table ${one.table_number}` : one.order_number;
    return `New order — ${where}`;
  }
  return `${arrived.length} new orders`;
}
